// Loads the signed-in user's own event history for Top Picks for You. Nothing
// here is new tracking — it only reads data Tonyte already records:
//
//   users/{uid}/interestedEvents   events the user marked interested
//   eventChats (memberIds)         events whose chat the user joined
//   eventAnalyticsRaw (userId)     event opens ("viewed") and shares — written
//                                  by utils/eventAnalytics.ts; readable only by
//                                  the user who wrote a row (see firestore.rules)
//   users/{uid}/blockedUsers       hosts to never recommend
//
// Every source is fetched independently (allSettled): if one fails — say the
// analytics rules aren't deployed yet — the others still personalise the
// carousel. Results are cached per user so the Events tab regaining focus does
// not re-read them.

import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS, firestore } from '../firebase';
import { normalizeActivity } from '../hooks/useFeed';
import { getResolvedEventId } from '../utils/eventIds';
import type { RecommendationHistory } from '../utils/eventRecommendations';
import type { TonyteActivity } from '../types';

export type UserEventSignals = {
  interestedIds: string[];
  joinedIds: string[];
  viewedIds: string[];
  sharedIds: string[];
  blockedUserIds: string[];
  // Sources that failed to load; the ranking simply has less to go on.
  failedSources: string[];
};

const SIGNALS_TTL_MS = 5 * 60 * 1000;
const EVENT_DOC_TTL_MS = 30 * 60 * 1000;
const INTERESTED_LIMIT = 100;
const JOINED_LIMIT = 100;
const ANALYTICS_LIMIT = 150;
const BLOCKED_LIMIT = 200;
// A history event outside the current upcoming pool has to be read once to
// learn its category/tags. Capped so a long-time user costs a bounded number of
// reads per cache window, strongest signals first.
const MAX_HISTORY_FETCHES = 30;

type SignalsCacheEntry = { promise: Promise<UserEventSignals>; loadedAt: number };
const signalsCache = new Map<string, SignalsCacheEntry>();
const eventDocCache = new Map<string, { activity: TonyteActivity | null; at: number }>();

function uniqueInOrder(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach(value => {
    if (value && !seen.has(value)) {
      seen.add(value);
      result.push(value);
    }
  });
  return result;
}

async function readInterestedIds(userId: string): Promise<string[]> {
  const snapshot = await getDocs(query(
    collection(firestore, FIRESTORE_COLLECTIONS.users, userId, 'interestedEvents'),
    limit(INTERESTED_LIMIT),
  ));
  return uniqueInOrder(snapshot.docs.map(d => {
    const data = d.data() as Record<string, unknown>;
    return String(data.activityId || data.eventId || d.id || '').trim();
  }));
}

async function readJoinedIds(userId: string): Promise<string[]> {
  const snapshot = await getDocs(query(
    collection(firestore, FIRESTORE_COLLECTIONS.eventChats),
    where('memberIds', 'array-contains', userId),
    limit(JOINED_LIMIT),
  ));
  return uniqueInOrder(snapshot.docs.map(d => String((d.data() as Record<string, unknown>).eventId || d.id).trim()));
}

async function readAnalyticsIds(userId: string): Promise<{ viewed: string[]; shared: string[] }> {
  const snapshot = await getDocs(query(
    collection(firestore, 'eventAnalyticsRaw'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(ANALYTICS_LIMIT),
  ));
  const viewed: string[] = [];
  const shared: string[] = [];
  snapshot.docs.forEach(d => {
    const data = d.data() as Record<string, unknown>;
    const eventId = typeof data.eventId === 'string' ? data.eventId : '';
    if (data.type === 'event_detail_open') viewed.push(eventId);
    else if (data.type === 'event_share') shared.push(eventId);
  });
  return { viewed: uniqueInOrder(viewed), shared: uniqueInOrder(shared) };
}

async function readBlockedUserIds(userId: string): Promise<string[]> {
  const snapshot = await getDocs(query(
    collection(firestore, FIRESTORE_COLLECTIONS.users, userId, 'blockedUsers'),
    limit(BLOCKED_LIMIT),
  ));
  return uniqueInOrder(snapshot.docs.map(d => String((d.data() as Record<string, unknown>).blockedUserId || d.id)));
}

async function fetchSignals(userId: string): Promise<UserEventSignals> {
  const [interested, joined, analytics, blocked] = await Promise.allSettled([
    readInterestedIds(userId),
    readJoinedIds(userId),
    readAnalyticsIds(userId),
    readBlockedUserIds(userId),
  ]);
  const failedSources: string[] = [];
  const unwrap = <T,>(result: PromiseSettledResult<T>, name: string, fallback: T): T => {
    if (result.status === 'fulfilled') return result.value;
    failedSources.push(name);
    if (__DEV__) console.warn(`[Tonyte][RN][TopPicks] signal source failed: ${name}`, result.reason);
    return fallback;
  };
  const analyticsValue = unwrap(analytics, 'analytics', { viewed: [], shared: [] });
  return {
    interestedIds: unwrap(interested, 'interested', []),
    joinedIds: unwrap(joined, 'joined', []),
    viewedIds: analyticsValue.viewed,
    sharedIds: analyticsValue.shared,
    blockedUserIds: unwrap(blocked, 'blocked', []),
    failedSources,
  };
}

export function loadUserEventSignals(userId: string, force = false): Promise<UserEventSignals> {
  const cached = signalsCache.get(userId);
  if (cached && !force && Date.now() - cached.loadedAt < SIGNALS_TTL_MS) return cached.promise;
  const entry: SignalsCacheEntry = { promise: fetchSignals(userId), loadedAt: Date.now() };
  signalsCache.set(userId, entry);
  return entry.promise;
}

export function invalidateUserEventSignals(userId?: string) {
  if (userId) signalsCache.delete(userId);
  else signalsCache.clear();
}

async function fetchEventById(eventId: string): Promise<TonyteActivity | null> {
  const cached = eventDocCache.get(eventId);
  if (cached && Date.now() - cached.at < EVENT_DOC_TTL_MS) return cached.activity;
  let activity: TonyteActivity | null = null;
  try {
    // Events live in `activities`; the other two PUBLIC_ACTIVITY_COLLECTIONS
    // are legacy read paths, not worth two extra reads per history entry.
    const snapshot = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.activities, eventId));
    if (snapshot.exists()) activity = normalizeActivity(snapshot.id, snapshot.data() as Record<string, unknown>, FIRESTORE_COLLECTIONS.activities);
  } catch {
    // Missing/forbidden history events just contribute nothing.
  }
  eventDocCache.set(eventId, { activity, at: Date.now() });
  return activity;
}

// Turns the id lists into full events. Ids already in the current feed pool
// cost nothing; the rest are read once (bounded) and cached.
export async function resolveEventHistory(
  signals: UserEventSignals,
  pool: TonyteActivity[],
): Promise<RecommendationHistory> {
  const byId = new Map<string, TonyteActivity>();
  pool.forEach(activity => {
    byId.set(activity.id, activity);
    byId.set(getResolvedEventId(activity), activity);
  });

  const missing = uniqueInOrder([
    ...signals.joinedIds, ...signals.interestedIds, ...signals.sharedIds, ...signals.viewedIds,
  ]).filter(id => !byId.has(id)).slice(0, MAX_HISTORY_FETCHES);
  const fetched = await Promise.all(missing.map(async id => [id, await fetchEventById(id)] as const));
  fetched.forEach(([id, activity]) => { if (activity) byId.set(id, activity); });

  const resolve = (ids: string[]) => ids
    .map(id => byId.get(id))
    .filter((activity): activity is TonyteActivity => Boolean(activity));
  return {
    joined: resolve(signals.joinedIds),
    interested: resolve(signals.interestedIds),
    shared: resolve(signals.sharedIds),
    viewed: resolve(signals.viewedIds),
  };
}
